import React, { useState } from 'react';

const KeyUploadModal = ({ isOpen, onClose, campaignId, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });
      rows.push(row);
    }

    return rows;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = parseCSV(event.target.result);
        setPreview(parsed.slice(0, 5)); // Show first 5 rows as preview

        // Validate required columns
        if (parsed.length > 0) {
          const firstRow = parsed[0];
          if (!firstRow.key && !firstRow.keycode && !firstRow.key_code) {
            setError('CSV must have a "key" or "keycode" column');
          }
        }
      } catch (err) {
        setError('Failed to parse CSV file');
      }
    };
    reader.readAsText(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const parsed = parseCSV(event.target.result);
            
            console.log('Parsed CSV:', parsed);
            
            // Send to backend
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${campaignId}/keys/upload`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({ keys: parsed })
            });

            const data = await response.json();
            console.log('Backend response:', data);

            if (!response.ok) {
              throw new Error(data.details || data.error || 'Upload failed');
            }

            onSuccess(data);
            onClose();
            setFile(null);
            setPreview([]);
            resolve();
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });
    } catch (err) {
      setError(err.message || 'Upload failed');
      console.error('Upload error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">📤 Upload Keys (CSV)</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Instructions */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">CSV Format</h3>
            <p className="text-sm text-blue-800 mb-2">Your CSV should have these columns:</p>
            <code className="text-xs bg-blue-100 px-2 py-1 rounded block">
              creator_name,platform,handle,key,sent_date,notes
            </code>
            <p className="text-xs text-blue-700 mt-2">
              Required: <strong>key</strong> | Optional: creator_name, platform, handle, sent_date, notes
            </p>
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select CSV File
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
            />
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-2">Preview (First 5 rows)</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(preview[0]).map((header) => (
                        <th key={header} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {preview.map((row, idx) => (
                      <tr key={idx}>
                        {Object.values(row).map((value, vIdx) => (
                          <td key={vIdx} className="px-3 py-2 text-gray-900 truncate max-w-[150px]">
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Total rows to upload: {file ? 'Calculating...' : 0}
              </p>
            </div>
          )}

          {/* Example */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-2">Example CSV</h3>
            <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto">
{`creator_name,platform,handle,key,sent_date,notes
Ninja,twitch,ninja,ABCDE-12345-FGHIJ,2025-01-15,Sent via email
Shroud,twitch,shroud,KLMNO-67890-PQRST,2025-01-15,Sent via Discord
PewDiePie,youtube,pewdiepie,UVWXY-11111-ZABCD,,Not sent yet`}
            </pre>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={loading || !file}
              className="px-4 py-2 bg-sc-pink text-white rounded-md hover:bg-sc-pink/90 disabled:bg-gray-400"
            >
              {loading ? 'Uploading...' : 'Upload Keys'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyUploadModal;
